import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  CreditCardIcon, 
  CheckCircleIcon,
  ExclamationTriangleIcon 
} from '@heroicons/react/24/outline';
import { usePaymentMethods } from '../../../../hooks/usePaymentMethods';
import { useAuth } from '../../../../context/AuthContext';
import paymentService from '../../../../services/paymentService';
import { getCardElementOptions } from '../../../../utils/paymentUtils';

interface AddPaymentMethodFormProps {
  onSuccess?: (paymentMethod: any) => void;
  onCancel?: () => void;
  setAsDefault?: boolean;
  showSetAsDefaultOption?: boolean;
}

const AddPaymentMethodForm: React.FC<AddPaymentMethodFormProps> = ({
  onSuccess,
  onCancel,
  setAsDefault = false,
  showSetAsDefaultOption = true
}) => {
  const { auth } = useAuth();
  const { addPaymentMethod, stripeInitialized } = usePaymentMethods();
  const cardElementRef = useRef<HTMLDivElement>(null);
  const [cardElement, setCardElement] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [makeDefault, setMakeDefault] = useState(setAsDefault);
  const [billingDetails, setBillingDetails] = useState({
    name: auth.user?.name || '',
    email: auth.user?.email || '',
    phone: '',
    address: {
      line1: '',
      city: '',
      postal_code: '',
      country: 'ES'
    }
  });

  useEffect(() => {
    if (stripeInitialized) {
      initializeStripeElements();
    }
    
    return () => {
      if (cardElement) {
        cardElement.destroy();
      }
    };
  }, [stripeInitialized]);

  const initializeStripeElements = async () => {
    if (!stripeInitialized) {
      setError('Sistema de pagos no inicializado. Por favor, recarga la página.');
      return;
    }

    try {
      const element = await paymentService.createCardElement(getCardElementOptions());
      setCardElement(element);

      // Mount the element
      if (cardElementRef.current) {
        element.mount(cardElementRef.current);
      }

      // Listen for changes
      element.on('change', (event: any) => {
        setCardComplete(event.complete);
        setCardError(event.error ? event.error.message : null);
      });

    } catch (error: any) {
      console.error('Failed to initialize Stripe elements:', error);
      setError('Error al cargar el formulario de pago. Por favor, recarga la página.');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!cardElement || !cardComplete) {
      setError('Por favor, completa la información de la tarjeta');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const success = await addPaymentMethod({
        cardElement,
        billingDetails
      }, makeDefault);

      if (success) {
        onSuccess?.({ id: 'new', brand: 'unknown', last4: '0000' }); // Placeholder data
      } else {
        setError('Error al guardar la tarjeta. Inténtalo de nuevo.');
      }
    } catch (error: any) {
      console.error('Error saving payment method:', error);
      setError(error.message || 'Error al guardar la tarjeta. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleBillingDetailsChange = (field: string, value: string) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setBillingDetails(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else {
      setBillingDetails(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl p-6 shadow-lg"
    >
      <div className="flex items-center gap-3 mb-6">
        <CreditCardIcon className="w-6 h-6 text-yellow-500" />
        <h3 className="text-xl font-semibold text-gray-900">
          Añadir Nueva Tarjeta
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Card Element */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Información de la Tarjeta
          </label>
          <div 
            ref={cardElementRef}
            className={`
              p-4 border-2 rounded-xl transition-colors
              ${cardError ? 'border-red-300' : cardComplete ? 'border-green-300' : 'border-gray-300'}
              focus-within:border-yellow-400
            `}
          />
          {cardError && (
            <p className="mt-2 text-sm text-red-600 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-4 h-4" />
              {cardError}
            </p>
          )}
          {cardComplete && !cardError && (
            <p className="mt-2 text-sm text-green-600 flex items-center gap-2">
              <CheckCircleIcon className="w-4 h-4" />
              Tarjeta válida
            </p>
          )}
        </div>

        {/* Billing Details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nombre del Titular
            </label>
            <input
              type="text"
              value={billingDetails.name}
              onChange={(e) => handleBillingDetailsChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
              placeholder="Nombre completo"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={billingDetails.email}
              onChange={(e) => handleBillingDetailsChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
              placeholder="email@ejemplo.com"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Teléfono (Opcional)
            </label>
            <input
              type="tel"
              value={billingDetails.phone}
              onChange={(e) => handleBillingDetailsChange('phone', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
              placeholder="+34 600 000 000"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Código Postal
            </label>
            <input
              type="text"
              value={billingDetails.address.postal_code}
              onChange={(e) => handleBillingDetailsChange('address.postal_code', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
              placeholder="28001"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Dirección
            </label>
            <input
              type="text"
              value={billingDetails.address.line1}
              onChange={(e) => handleBillingDetailsChange('address.line1', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
              placeholder="Calle, número, piso..."
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Ciudad
            </label>
            <input
              type="text"
              value={billingDetails.address.city}
              onChange={(e) => handleBillingDetailsChange('address.city', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
              placeholder="Madrid"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              País
            </label>
            <select
              value={billingDetails.address.country}
              onChange={(e) => handleBillingDetailsChange('address.country', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 
                       focus:ring-yellow-400 focus:border-transparent"
            >
              <option value="ES">España</option>
              <option value="FR">Francia</option>
              <option value="PT">Portugal</option>
              <option value="IT">Italia</option>
              <option value="DE">Alemania</option>
            </select>
          </div>
        </div>

        {/* Set as Default Option */}
        {showSetAsDefaultOption && (
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="makeDefault"
              checked={makeDefault}
              onChange={(e) => setMakeDefault(e.target.checked)}
              className="w-4 h-4 text-yellow-500 border-gray-300 rounded focus:ring-yellow-400"
            />
            <label htmlFor="makeDefault" className="text-sm text-gray-700">
              Establecer como tarjeta por defecto
            </label>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-center gap-2">
              <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 pt-4">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl
                     hover:bg-gray-50 transition-colors font-medium"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={loading || !cardComplete || !!cardError}
            className="flex-1 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black 
                     rounded-xl font-medium transition-colors disabled:opacity-50 
                     disabled:cursor-not-allowed"
          >
            {loading ? 'Guardando...' : 'Guardar Tarjeta'}
          </button>
        </div>
      </form>

      {/* Security Notice */}
      <div className="mt-6 p-4 bg-gray-50 rounded-xl">
        <p className="text-xs text-gray-600 text-center">
          🔒 Tu información está protegida con cifrado SSL y no almacenamos los datos de tu tarjeta.
          Los pagos son procesados de forma segura por Stripe.
        </p>
      </div>
    </motion.div>
  );
};

export default AddPaymentMethodForm;